import React, { useState, useEffect, useCallback } from 'react';
import emailApi from '../services/email';
import { concessions as concessionsApi } from '../services/concessions';
import './ConfigEmail.css';

import { texteTheme } from '../utils/theme';
// ─── Config statique des alertes ────────────────────────────────────────────

// ⚠️ Les conditions de déclenchement ne sont PAS listées ici : elles viennent de
// GET /api/email/alerte-conditions (backend/src/jobs/alerte-conditions.js), la
// même source que le pied des emails envoyés. Une copie locale finissait par
// diverger du filtre réellement appliqué par les jobs.
const ALERT_TYPES = [
  {
    type_rapport: 'rapport_quotidien',
    label: 'Rapport quotidien — Interventions à domicile',
    schedule: 'Lun-Ven 07h00 (heure Martinique)',
    perConcession: true,
    placeholders: ['{{liste}}'],
    color: 'var(--color-info)',
  },
  {
    type_rapport: 'alerte_rdv_j3',
    label: 'Alerte RDV J+3',
    schedule: 'Lun-Ven 07h00 (heure Martinique)',
    perConcession: true,
    placeholders: ['{{liste}}', '{{date}}'],
    color: 'var(--color-warning)',
  },
  // Scindée en deux le 2026-09-09 : l'ancienne clé `alerte_pret_vh_j1` n'est
  // plus envoyée (aucun cron ne la lit) et disparaît donc de cet écran.
  {
    type_rapport: 'alerte_pret_vh_sans_rdv',
    label: 'Alerte Prêt VH — aucun RDV proposé',
    schedule: 'Lun-Ven 07h00 (heure Martinique)',
    perConcession: true,
    placeholders: ['{{liste}}'],
    color: 'var(--color-accent)',
  },
  {
    type_rapport: 'alerte_pret_vh_rdv_lointain',
    label: 'Alerte Prêt VH — RDV après 15 jours',
    schedule: 'Lun-Ven 07h00 (heure Martinique)',
    perConcession: true,
    placeholders: ['{{liste}}'],
    color: 'var(--color-accent)',
  },
  {
    type_rapport: 'alerte_remorquage',
    label: 'Alerte Remorquage — Martinique (972)',
    schedule: 'Lun-Ven 07h00 (heure Martinique)',
    perConcession: true,
    placeholders: ['{{liste}}'],
    color: 'var(--color-decision)',
  },
  {
    type_rapport: 'alerte_vh_pret',
    label: 'Alerte VH Prêt — demande enregistrée',
    // Seule alerte Takata non planifiée : elle part à la saisie de l'action.
    schedule: 'À chaque action « VH Prêt » (différée au lundi si saisie le week-end)',
    perConcession: true,
    placeholders: ['{{liste}}'],
    color: 'var(--color-info)',
  },
];

const TYPE_LABELS = {
  test: 'Test',
  manuel: 'Manuel',
  alerte_lead_web: 'Alerte lead web',
  rapport_quotidien: 'Rapport quotidien',
  alerte_rdv_j3: 'Alerte RDV J+3',
  alerte_pret_vh_j1: 'Alerte Prêt VH (historique)',
  alerte_pret_vh_sans_rdv: 'Alerte Prêt VH — aucun RDV',
  alerte_pret_vh_rdv_lointain: 'Alerte Prêt VH — RDV > 15 j',
  alerte_remorquage: 'Alerte Remorquage',
  alerte_vh_pret: 'Alerte VH Prêt',
  rappel: 'Rappel',
};

const STATUT_LABELS = {
  envoye: { label: 'Envoyé', color: 'var(--color-success)' },
  erreur: { label: 'Erreur', color: 'var(--color-error)' },
  desactive: { label: 'Désactivé', color: 'var(--text-muted)' },
};

// ─── Composant AlertCard ─────────────────────────────────────────────────────

function AlertCard({ alertConfig, destinataires, concessionsListe, templates, conditionsParType, editMode, isSuperAdmin, onAddDest, onDeleteDest, onSaveTemplate }) {
  const { type_rapport, label, schedule, perConcession, placeholders, color } = alertConfig;
  const { conditions = [], note = null } = conditionsParType[type_rapport] || {};

  const [newEmail, setNewEmail] = useState('');
  const [newConcessionId, setNewConcessionId] = useState('');
  const [adding, setAdding] = useState(false);

  const [tplEdit, setTplEdit] = useState(null); // null = lecture, { sujet, template_text } = édition
  const [saving, setSaving] = useState(false);

  const tpl = templates[type_rapport];

  // Filtrer les destinataires pour ce type d'alerte
  const myDests = destinataires.filter(d => d.type_rapport === type_rapport);

  // Grouper par concession. Les destinataires sans concession sur une alerte
  // cloisonnée sont des reliquats : ils ne reçoivent plus rien.
  const ORPHAN_KEY = '⚠️ Sans concession — ne reçoit aucun email';
  const groupedDests = {};
  for (const d of myDests) {
    const key = d.concession_nom || (perConcession ? ORPHAN_KEY : 'Global');
    if (!groupedDests[key]) groupedDests[key] = [];
    groupedDests[key].push(d);
  }

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    await onAddDest({ email: newEmail.trim(), type_rapport, concession_id: newConcessionId || null });
    setNewEmail('');
    setAdding(false);
  };

  const handleSave = async () => {
    if (!tplEdit) return;
    setSaving(true);
    await onSaveTemplate(type_rapport, tplEdit);
    setTplEdit(null);
    setSaving(false);
  };

  return (
    <div className="alerte-card">
      {/* Header */}
      <div className="alerte-card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="alerte-card-dot" style={{ background: color }} />
          <span className="alerte-card-title">{label}</span>
        </div>
        <span className="alerte-card-schedule">⏰ {schedule}</span>
      </div>

      {/* Conditions — servies par le backend, identiques au pied des emails envoyés */}
      <div className="alerte-card-section">
        <div className="alerte-section-label">Conditions de déclenchement</div>
        {conditions.length === 0 ? (
          <div className="alerte-empty-dest">Conditions indisponibles</div>
        ) : (
          <ul className="alerte-conditions-list">
            {conditions.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        )}
        {note && <div className="alerte-conditions-note">{note}</div>}
      </div>

      {/* Destinataires */}
      <div className="alerte-card-section">
        <div className="alerte-section-label">
          Destinataires
          {perConcession && <span className="alerte-badge-note">· par concession</span>}
        </div>

        {Object.keys(groupedDests).length === 0 && (
          <div className="alerte-empty-dest">Aucun destinataire configuré</div>
        )}

        {Object.entries(groupedDests).map(([concNom, dests]) => (
          <div key={concNom} className="alerte-concession-group">
            <div
              className="alerte-concession-name"
              style={concNom === ORPHAN_KEY ? { color: 'light-dark(var(--color-warning), var(--color-warning-dark))' } : undefined}
            >
              {concNom}
            </div>
            {dests.map(d => (
              <div key={d.id} className="alerte-dest-row">
                <span className="alerte-dest-email">{d.email}</span>
                {concNom === ORPHAN_KEY && (
                  <span className="alerte-dest-orphan">ne reçoit rien</span>
                )}
                {editMode && isSuperAdmin && (
                  <button className="btn-icon delete" title="Supprimer" onClick={() => onDeleteDest(d.id)}>🗑️</button>
                )}
              </div>
            ))}
          </div>
        ))}

        {editMode && isSuperAdmin && (
          <div className="alerte-add-form">
            <input
              type="email"
              className="page-search"
              style={{ width: 220, flexShrink: 0 }}
              placeholder="adresse@exemple.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
            {perConcession && (
              <select className="page-select" value={newConcessionId} onChange={e => setNewConcessionId(e.target.value)}>
                <option value="">Sélectionner une concession</option>
                {concessionsListe.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            )}
            <button className="btn-primary" onClick={handleAdd} disabled={adding || !newEmail.trim() || (perConcession && !newConcessionId)}>
              {adding ? 'Ajout…' : '+ Ajouter'}
            </button>
          </div>
        )}
      </div>

      {/* Modèle email */}
      <div className="alerte-card-section alerte-card-section-last">
        <div className="alerte-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Modèle d'email</span>
          {editMode && isSuperAdmin && !tplEdit && tpl && (
            <button className="alerte-edit-btn" onClick={() => setTplEdit({ sujet: tpl.sujet, template_text: tpl.template_text })}>
              ✏️ Modifier
            </button>
          )}
        </div>

        {!tpl && <div className="alerte-empty-dest">Chargement du modèle…</div>}

        {tpl && !tplEdit && (
          <div className="alerte-tpl-preview">
            <div className="alerte-tpl-row">
              <span className="alerte-tpl-label">Objet</span>
              <span className="alerte-tpl-value">{tpl.sujet}</span>
            </div>
            <div className="alerte-tpl-row alerte-tpl-body-row">
              <span className="alerte-tpl-label">Corps</span>
              <pre className="alerte-tpl-body">{tpl.template_text}</pre>
            </div>
            {placeholders.length > 0 && (
              <div className="alerte-tpl-placeholders">
                Variables disponibles : {placeholders.map(p => <code key={p}>{p}</code>)}
              </div>
            )}
          </div>
        )}

        {tplEdit && (
          <div className="alerte-tpl-form">
            <label className="alerte-tpl-field-label">Objet</label>
            <input
              type="text"
              className="page-search"
              style={{ width: '100%' }}
              value={tplEdit.sujet}
              onChange={e => setTplEdit(prev => ({ ...prev, sujet: e.target.value }))}
            />
            <label className="alerte-tpl-field-label" style={{ marginTop: 10 }}>
              Corps
              <span className="alerte-tpl-placeholders" style={{ display: 'inline', marginLeft: 8 }}>
                Variables : {placeholders.map(p => <code key={p}>{p}</code>)}
              </span>
            </label>
            <textarea
              className="alerte-tpl-textarea"
              value={tplEdit.template_text}
              onChange={e => setTplEdit(prev => ({ ...prev, template_text: e.target.value }))}
              rows={8}
            />
            <div className="alerte-tpl-form-actions">
              <button className="btn-secondary" onClick={() => setTplEdit(null)} disabled={saving}>Annuler</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving || !tplEdit.sujet.trim() || !tplEdit.template_text.trim()}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Composant principal ─────────────────────────────────────────────────────

export default function ConfigEmail({ user, editMode }) {
  const [activeTab, setActiveTab] = useState('alertes');

  // Données globales
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // Alertes
  const [destinataires, setDestinataires] = useState([]);
  const [concessionsListe, setConcessionsListe] = useState([]);
  const [templates, setTemplates] = useState({});
  const [conditionsParType, setConditionsParType] = useState({});

  // Historique
  const [history, setHistory] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState(new Set());

  // Test email
  const [testTo, setTestTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const isSuperAdmin = user?.role === 'super-admin';

  // ── Chargement ──────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    try {
      const [statusRes, histRes, destRes, concessRes, tplRes, condRes] = await Promise.all([
        emailApi.getStatus(),
        emailApi.getHistory({ limit: 100, ...(filterType && { type: filterType }), ...(filterStatut && { statut: filterStatut }) }),
        emailApi.getRapportDestinataires(),
        concessionsApi.getAll(),
        emailApi.getAlerteTemplates(),
        emailApi.getAlerteConditions(),
      ]);
      setStatus(statusRes.data);
      setHistory(histRes.data.history || []);
      setDestinataires(destRes.data.destinataires || []);
      setConcessionsListe(concessRes.data.concessions || []);
      const tplMap = {};
      for (const t of (tplRes.data.templates || [])) tplMap[t.type_rapport] = t;
      setTemplates(tplMap);
      setConditionsParType(condRes.data.conditions || {});
      setTestTo(prev => prev || user?.email || '');
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Erreur de chargement' });
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatut, user?.email]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Handlers destinataires ──────────────────────────────────────────────────

  const handleAddDest = async (payload) => {
    try {
      const res = await emailApi.addRapportDestinataire(payload);
      const conc = concessionsListe.find(c => String(c.id) === String(payload.concession_id));
      setDestinataires(prev => [...prev, { ...res.data, concession_nom: conc?.nom || null }]);
      setMsg({ type: 'ok', text: 'Destinataire ajouté.' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || "Erreur d'ajout" });
    }
  };

  const handleDeleteDest = async (id) => {
    if (!window.confirm('Supprimer ce destinataire ?')) return;
    try {
      await emailApi.deleteRapportDestinataire(id);
      setDestinataires(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Erreur de suppression' });
    }
  };

  // ── Handlers templates ──────────────────────────────────────────────────────

  const handleSaveTemplate = async (typeRapport, data) => {
    try {
      const res = await emailApi.updateAlerteTemplate(typeRapport, data);
      setTemplates(prev => ({ ...prev, [typeRapport]: res.data }));
      setMsg({ type: 'ok', text: 'Modèle mis à jour.' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Erreur de sauvegarde' });
    }
  };

  // ── Handlers historique ─────────────────────────────────────────────────────

  const handleSendTest = async () => {
    if (!testTo.trim()) { setMsg({ type: 'err', text: 'Adresse requise' }); return; }
    setSendingTest(true); setMsg(null);
    try {
      await emailApi.sendTest(testTo.trim());
      setMsg({ type: 'ok', text: `Email de test envoyé à ${testTo.trim()}` });
      setTimeout(() => setMsg(null), 5000);
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || "Erreur lors de l'envoi" });
    } finally { setSendingTest(false); }
  };

  const openDetail = async (id) => {
    try { const res = await emailApi.getHistoryDetail(id); setDetail(res.data.entry); }
    catch (e) { setMsg({ type: 'err', text: 'Erreur de chargement du détail' }); }
  };

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette entrée de l'historique ?")) return;
    try { await emailApi.delete(id); setSelected(prev => { const n = new Set(prev); n.delete(id); return n; }); await loadAll(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const handleDeleteMany = async () => {
    if (!selected.size || !window.confirm(`Supprimer ${selected.size} entrée(s) ?`)) return;
    try { await emailApi.deleteMany(Array.from(selected)); setSelected(new Set()); await loadAll(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const filteredHistory = history.filter(h => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (h.to_email || '').toLowerCase().includes(q)
      || (h.subject || '').toLowerCase().includes(q)
      || (h.to_user_nom || '').toLowerCase().includes(q)
      || (h.type || '').toLowerCase().includes(q);
  });

  // ── Rendu ───────────────────────────────────────────────────────────────────

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Chargement…</div>;

  return (
    <div className="page-container">

      {/* Bandeau statut + test */}
      <div className="ce-status-bar">
        <div className="ce-status-item">
          <div className="ce-status-label">État</div>
          <div style={{ fontWeight: 600, color: status?.actif ? 'var(--color-success)' : 'var(--color-warning)' }}>
            {status?.actif ? '✅ Actif' : '⚠️ Désactivé'}
          </div>
        </div>
        <div className="ce-status-item">
          <div className="ce-status-label">Clé API Brevo</div>
          <div style={{ fontWeight: 600, color: status?.api_key_configured ? 'var(--color-success)' : 'var(--color-error)' }}>
            {status?.api_key_configured ? 'Configurée' : 'Manquante'}
          </div>
        </div>
        <div className="ce-status-item">
          <div className="ce-status-label">Expéditeur</div>
          <div style={{ fontWeight: 600 }}>{status?.from_name || '—'}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{status?.from_address || '—'}</div>
        </div>
        <div className="ce-status-item">
          <div className="ce-status-label">Reply-to</div>
          <div style={{ fontWeight: 600 }}>{status?.reply_to || '—'}</div>
        </div>
        <div className="ce-test-form">
          <strong style={{ fontSize: 13, whiteSpace: 'nowrap' }}>📤 Email de test :</strong>
          <input type="email" className="page-search" style={{ width: 220 }} placeholder="destinataire@exemple.com"
            value={testTo} onChange={e => setTestTo(e.target.value)} />
          <button className="btn-primary" onClick={handleSendTest} disabled={sendingTest || !status?.actif}>
            {sendingTest ? 'Envoi…' : 'Envoyer'}
          </button>
        </div>
      </div>

      {/* Message global */}
      {msg && (
        <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, background: msg.type === 'ok' ? 'var(--color-success-light)' : 'var(--color-error-light)', color: msg.type === 'ok' ? 'var(--color-success)' : 'var(--color-error)' }}>
          {msg.text}
        </div>
      )}

      {/* Tabs */}
      <div className="page-tabs">
        <button className={activeTab === 'alertes' ? 'active' : ''} onClick={() => setActiveTab('alertes')}>
          📋 Alertes & destinataires
        </button>
        <button className={activeTab === 'historique' ? 'active' : ''} onClick={() => setActiveTab('historique')}>
          📧 Historique des envois
        </button>
      </div>

      {/* ── Tab Alertes ─────────────────────────────────────────────────────── */}
      {activeTab === 'alertes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {ALERT_TYPES.map(alertConfig => (
            <AlertCard
              key={alertConfig.type_rapport}
              alertConfig={alertConfig}
              destinataires={destinataires}
              concessionsListe={concessionsListe}
              templates={templates}
              conditionsParType={conditionsParType}
              editMode={editMode}
              isSuperAdmin={isSuperAdmin}
              onAddDest={handleAddDest}
              onDeleteDest={handleDeleteDest}
              onSaveTemplate={handleSaveTemplate}
            />
          ))}
        </div>
      )}

      {/* ── Tab Historique ──────────────────────────────────────────────────── */}
      {activeTab === 'historique' && (
        <>
          {/* Toolbar */}
          <div className="page-toolbar">
            <input type="text" className="page-search" placeholder="🔍 Rechercher…" value={search} onChange={e => setSearch(e.target.value)} />
            <select className={`page-select ${filterType ? 'active-filter' : ''}`} value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">Tous les types</option>
              {Object.entries(TYPE_LABELS).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
            <select className={`page-select ${filterStatut ? 'active-filter' : ''}`} value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
              <option value="">Tous les statuts</option>
              {Object.entries(STATUT_LABELS).map(([code, { label }]) => <option key={code} value={code}>{label}</option>)}
            </select>
            {editMode && isSuperAdmin && selected.size > 0 && (
              <button className="btn-danger" onClick={handleDeleteMany} style={{ marginLeft: 'auto' }}>
                🗑️ Supprimer ({selected.size})
              </button>
            )}
          </div>

          {/* Table */}
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {editMode && isSuperAdmin && (
                    <th style={{ width: 32, textAlign: 'center' }}>
                      <input type="checkbox"
                        checked={filteredHistory.length > 0 && filteredHistory.every(h => selected.has(h.id))}
                        onChange={e => { if (e.target.checked) setSelected(new Set(filteredHistory.map(h => h.id))); else setSelected(new Set()); }} />
                    </th>
                  )}
                  <th>Date</th>
                  <th>Destinataire</th>
                  <th>Sujet</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'center' }}>Statut</th>
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length === 0 && (
                  <tr><td colSpan={editMode && isSuperAdmin ? 7 : 6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Aucun envoi</td></tr>
                )}
                {filteredHistory.map(h => {
                  const statutMeta = STATUT_LABELS[h.statut] || { label: h.statut, color: 'var(--text-muted)' };
                  return (
                    <tr key={h.id}>
                      {editMode && isSuperAdmin && (
                        <td style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={selected.has(h.id)} onChange={() => toggleSelect(h.id)} />
                        </td>
                      )}
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {new Date(h.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{h.to_email}</div>
                        {h.to_user_nom && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{h.to_user_prenom} {h.to_user_nom}</div>}
                      </td>
                      <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.subject}</td>
                      <td>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: 'var(--bg-muted)', fontSize: 11, fontWeight: 500 }}>
                          {TYPE_LABELS[h.type] || h.type || '—'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', color: texteTheme(statutMeta.color), fontWeight: 600, fontSize: 12 }}>{statutMeta.label}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-icon" title="Voir le contenu" onClick={() => openDetail(h.id)}>👁️</button>
                          {editMode && isSuperAdmin && (
                            <button className="btn-icon delete" title="Supprimer" onClick={() => handleDelete(h.id)}>🗑️</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modale détail email */}
      {detail && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📧 Détail de l'envoi #{detail.id}</h2>
              <button className="modal-close" onClick={() => setDetail(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 14px', fontSize: 13, marginBottom: 16 }}>
                <div style={{ color: 'var(--text-muted)' }}>Date :</div>
                <div>{new Date(detail.created_at).toLocaleString('fr-FR')}</div>
                <div style={{ color: 'var(--text-muted)' }}>Destinataire :</div>
                <div>{detail.to_email}{detail.to_user_nom && <span style={{ color: 'var(--text-muted)' }}> ({detail.to_user_prenom} {detail.to_user_nom})</span>}</div>
                <div style={{ color: 'var(--text-muted)' }}>Sujet :</div>
                <div style={{ fontWeight: 600 }}>{detail.subject}</div>
                <div style={{ color: 'var(--text-muted)' }}>Type :</div>
                <div>{TYPE_LABELS[detail.type] || detail.type || '—'}</div>
                <div style={{ color: 'var(--text-muted)' }}>Statut :</div>
                <div style={{ color: texteTheme(STATUT_LABELS[detail.statut]?.color), fontWeight: 600 }}>{STATUT_LABELS[detail.statut]?.label || detail.statut}</div>
                {detail.provider_message_id && <>
                  <div style={{ color: 'var(--text-muted)' }}>Message ID :</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{detail.provider_message_id}</div>
                </>}
                {detail.erreur && <>
                  <div style={{ color: 'var(--text-muted)' }}>Erreur :</div>
                  <div style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', fontFamily: 'monospace', fontSize: 12 }}>{detail.erreur}</div>
                </>}
              </div>
              {detail.body_html ? (
                <>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Aperçu HTML</div>
                  <iframe title="email-preview" srcDoc={detail.body_html} sandbox=""
                    style={{ width: '100%', minHeight: 300, border: '1px solid var(--border-default)', borderRadius: 8 }} />
                </>
              ) : detail.body_text ? (
                <>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Texte</div>
                  <pre style={{ background: 'var(--bg-muted)', padding: 12, borderRadius: 8, whiteSpace: 'pre-wrap', fontSize: 12 }}>{detail.body_text}</pre>
                </>
              ) : null}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setDetail(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
